import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useMutation } from '@tanstack/react-query';
import { Layout, Image, Result, Typography, Space, Button } from 'antd';
import { MailTwoTone } from '@ant-design/icons';
import { API_BASE_URL, COMPANY_NAME } from '../../../constants';
import { getLogoUrl } from '../../../utils/url';
import { t } from '../../../utils/i18n';

const StyledContent = styled(Layout.Content)`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;
  padding: 50px;
  background: #f8f8f8;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const StyledHeaderContainer = styled.div`
  padding: 16px;
  margin-bottom: 20px;
`;

const StyledBodyContainer = styled.div`
  width: 100%;
  max-width: 650px;
  padding: 25px;
  overflow: hidden;
  margin: 0 auto;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  background: #fff;
  border-radius: 4px;
  border: 1px solid #e5e7e9;

  @media (max-width: 768px) {
    padding: 15px 5px;
  }
`;

export function EmailVerificationPage() {
  const logoUrl = getLogoUrl(COMPANY_NAME);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);

  const { mutate: mutateSendEmail } = useMutation({
    mutationKey: ['email_confirmation'],
    mutationFn: () => {
      const emailVerificationUrl = `${API_BASE_URL}/v2/email_confirmation/resend`;
      return fetch(emailVerificationUrl, {
        method: 'POST',
        credentials: 'include',
      });
    },
  });

  const handleOnClickSendEmail = () => {
    setIsTimerActive(true);
    setTimeLeft(60);
    mutateSendEmail();
  };

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | undefined;
    if (isTimerActive && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prevTime) => prevTime - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setIsTimerActive(false);
    }
    return () => clearInterval(timer);
  }, [isTimerActive, timeLeft]);

  return (
    <StyledContent>
      <StyledHeaderContainer>
        <Image alt="logo" src={logoUrl} height={25} preview={false} />
      </StyledHeaderContainer>
      <StyledBodyContainer>
        <Result
          icon={<MailTwoTone twoToneColor="#00d0be" />}
          title={
            <Typography.Title level={4}>
              {t('email-verification.title')}
            </Typography.Title>
          }
          subTitle={
            <Space direction="vertical" size="middle">
              <Typography.Text type="secondary">
                {t('email-verification.description')}
              </Typography.Text>
              {isTimerActive && (
                <Typography.Text type="secondary" strong>
                  {t('email-verification.retry-counter', {
                    count: timeLeft,
                  })}
                </Typography.Text>
              )}
            </Space>
          }
          extra={[
            <Button
              type="primary"
              key="email-verification"
              disabled={isTimerActive}
              onClick={handleOnClickSendEmail}
            >
              {t('email-verification.button')}
            </Button>,
          ]}
        />
        {COMPANY_NAME === 'shore' && (
          <Typography.Text type="secondary" style={{ fontSize: 10 }}>
            {t('email-verification.support-question-text')}{' '}
            <Typography.Link
              href="https://help.shore.com/"
              target="_blank"
              style={{
                fontSize: '10px',
                color: '#222222',
                textDecoration: 'underline',
              }}
            >
              {t('email-verification.support-link-text')}
            </Typography.Link>
          </Typography.Text>
        )}
      </StyledBodyContainer>
    </StyledContent>
  );
}
